import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useMunicipalComunicados } from './hooks/useMunicipalComunicados';
import { useMunicipalConfig } from './hooks/useMunicipalConfig';
import { useCrud } from '../../api/hooks/useCrud';
import { ComunicadosCenterCore } from '../../components/comunicados/ComunicadosCenterCore';
import type {
  ComunicadosConfig,
  ComunicadoItem,
  ComunicadoWizardData,
} from '../../components/comunicados/ComunicadosConfig';

interface Escola {
  id: string;
  nome: string;
  ativo: boolean;
}

export function ComunicadosMunicipal() {
  const location = useLocation();
  const { user } = useAuthStore();
  const { canManage } = usePermissions();
  const municipioId = user?.municipioId || '';
  const { config: muniConfig } = useMunicipalConfig();
  const { data: escolas = [] } = useCrud<Escola>('escolas');
  const {
    data: comunicados = [],
    isLoading,
    create,
    update,
    deactivate,
  } = useMunicipalComunicados();

  // Captura draft se veio do Banco de Mídias via location.state
  const initialDraft = useMemo(() => {
    const fromMidia = (location.state as any)?.createFromMidia;
    if (!fromMidia) return null;
    return {
      titulo: fromMidia.titulo || '',
      categoria: fromMidia.categoria || 'INSTITUCIONAL',
      tipo: 'AVISO',
      destinatario: 'TODOS',
      corpo: fromMidia.descricao || '',
      configPreview: {
        midiaUrl: fromMidia.midiaUrl,
        midiaTitulo: fromMidia.titulo,
      },
    };
  }, [location.state]);

  const config: ComunicadosConfig = useMemo(
    () => ({
      scope: 'MUNICIPAL',
      scopeId: municipioId,
      branding: {
        tenantName:
          muniConfig?.nomeOficial ||
          (muniConfig?.nome ? `Prefeitura Municipal de ${muniConfig.nome}` : 'Prefeitura Municipal'),
        tenantBadge: muniConfig?.nomeSecretario
          ? `Secretaria de Educação — ${muniConfig.nomeSecretario}`
          : 'Secretaria Municipal de Educação',
        logoUrl: muniConfig?.logoUrl,
        primaryColor: muniConfig?.corPrimaria || '#1e3a8a',
        secondaryColor: muniConfig?.corSecundaria || '#059669',
        slogan: muniConfig?.slogan,
        autoridadeNome: muniConfig?.nomePrefeito,
        autoridadeCargo: muniConfig?.cargoPrefeito || 'Prefeito Municipal',
      },
      labels: {
        heroTitle: 'Central de Comunicados Municipal',
        heroSubtitle:
          'Painel de avisos, campanhas oficiais e eventos da rede. Dispare comunicados em lote com tracking de leitura pelas famílias.',
        searchPlaceholder: 'Buscar comunicados da rede municipal...',
        emptyTitle: 'Nenhum comunicado municipal criado ainda',
        emptySubtitle:
          'Inicie o envio de avisos e campanhas institucionais para todas as escolas ou turmas da rede municipal.',
      },
      features: {
        multiSchoolSelect: true,
        turmaSelect: false,
        templates: true,
        metricsCards: true,
        calendarView: true,
        whatsappPreview: true,
        fixarComunicado: true,
        leituraAudit: true,
        canManage: canManage('comunicados'),
      },
      escolas: escolas.map((e: Escola) => ({ id: e.id, nome: e.nome })),
    }),
    [municipioId, muniConfig, escolas],
  );

  return (
    <ComunicadosCenterCore
      config={config}
      comunicados={comunicados as unknown as ComunicadoItem[]}
      isLoading={isLoading}
      onCreate={(data: ComunicadoWizardData) => create.mutateAsync(data as any)}
      onUpdate={(id: string, data: Partial<ComunicadoWizardData>) =>
        update.mutateAsync({ id, ...data } as any)
      }
      onRemove={(id: string) => deactivate.mutateAsync(id)}
      onSendDraft={(c: ComunicadoItem) =>
        update.mutateAsync({ id: c.id, status: 'ENVIADO' } as any)
      }
      initialDraft={initialDraft}
    />
  );
}
